import classnames from 'classnames';
import type { FunctionComponent, MouseEvent, ReactNode } from 'react';

import Button from '../Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../Button/button-constants';
import Flex from '../Flex';
import Icon from '../Icon';
import type { ButtonType } from '../Button/button-constants';
import type { IconClassName } from '../Icon';

import styles from './CardBanner.module.less';

type CardBannerProps = {
  /**
   * Optional additional className(s) to apply to the root container
   */
  className?: string;
  /**
   * Determines the theme of the CardBanner.
   */
  variant?: CardBannerVariant;
  /**
   * Title for the CardBanner
   */
  title?: string;
  /**
   * Description for the CardBanner
   */
  description?: ReactNode;
  /**
   * Icon to be displayed beside the title.
   */
  icon?: IconClassName;
  /**
   * Call To Action
   */
  cta?: {
    text: string,
    onClick: (event: MouseEvent<HTMLElement>) => void,
    isFullWidth?: boolean;
    icon?: IconClassName;
  },
  /**
   * Determines whether to stack the header & cta vertically
   */
  stackHeaderCta?: boolean;
  /**
   * Optional image to display at the very top of the banner (above the title).
   */
  headerImage?: {
    src: string;
    alt?: string;
  };
  /**
   * Adopted Child Pattern: pass any overrides keyed by CardBanner's internal class local names.
   * Keep this generic to reduce boilerplate; extra keys are ignored.
   */
  classes?: Partial<Record<string, string>>;
};

export enum CardBannerVariant {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  DANGER = 'danger',
  PROMO = 'promo',
}

const mapVariantClass = new Map<CardBannerVariant, string>([
  [CardBannerVariant.SUCCESS, styles.cardBannerSuccess],
  [CardBannerVariant.INFO, styles.cardBannerInfo],
  [CardBannerVariant.WARNING, styles.cardBannerWarning],
  [CardBannerVariant.DANGER, styles.cardBannerDanger],
  [CardBannerVariant.PROMO, styles.cardBannerPromo],
]);

const mapButtonType = new Map<CardBannerVariant, ButtonType>([
  [CardBannerVariant.SUCCESS, BUTTON_TYPE.PRIMARY],
  [CardBannerVariant.INFO, BUTTON_TYPE.SECONDARY],
  [CardBannerVariant.WARNING, BUTTON_TYPE.DARK],
  [CardBannerVariant.DANGER, BUTTON_TYPE.ERROR],
  [CardBannerVariant.PROMO, BUTTON_TYPE.DARK],
]);

/**
 * A CardBanner component that blends card with banner styling, featuring a bordered design and lighter background with optional CTA.
 * Supports different visual variants (defaults to "success").
 * All props are optional!
 *
 * @example simple
 * <CardBanner title="MyTitle" />
 * @example with ReactNode
 * <CardBanner title="MyTitle"><>MyChildren</></CardBanner>
 */
const CardBanner: FunctionComponent<CardBannerProps> = ({
  className,
  title,
  description,
  icon,
  cta,
  variant = CardBannerVariant.SUCCESS,
  stackHeaderCta = true,
  headerImage,
  classes,
  children,
}) => {
  const variantClassName = mapVariantClass.get(variant);
  const cardBannerClass = classnames(
    styles.cardBanner,
    variantClassName,
    classes?.cardBanner,
    className,
  );
  const buttonType = mapButtonType.get(variant);

  return (
    <Flex
      direction="column"
      align="flex-start"
      className={cardBannerClass}
    >
      <Flex
        direction={stackHeaderCta ? 'column' : 'row'}
        align="flex-start"
        justify="space-between"
        gap={16}
        className={classnames(styles.cardBannerContainer, classes?.cardBannerContainer)}
      >
        <Flex direction="column" align="flex-start" gap={8} grow={3}>
          <div className={classnames(styles.cardBannerHeader, classes?.cardBannerHeader)}>
            {headerImage?.src && (
              <div className={classnames(styles.cardBannerHeaderImage, classes?.cardBannerHeaderImage)}>
                <img
                  src={headerImage.src}
                  alt={headerImage.alt || ''}
                  />
              </div>
            )}
            <div className={classnames(styles.cardBannerHeaderContent, classes?.cardBannerHeaderContent)}>
              {icon && <Icon icon={icon} />}
              {title && (
                <div className={classnames(styles.cardBannerHeaderTitle, classes?.cardBannerHeaderTitle)}>{title}</div>
              )}
            </div>
          </div>
          {description && (
            <div className={classnames(styles.cardBannerDescription, classes?.cardBannerDescription)}>
              {description}
            </div>
          )}
        </Flex>
        {cta &&
          <Button
            type={buttonType}
            size={BUTTON_SIZE.MEDIUM}
            icon={cta.icon}
            onClick={cta.onClick}
            isFullWidth={cta?.isFullWidth}
          >{cta.text}
          </Button>}
      </Flex>

      {children && (
        <>
          <hr className={classnames(styles.cardBannerDivider, classes?.cardBannerDivider)} />
          {children}
        </>
      )}

    </Flex>
  );
};

export default CardBanner;
